import React, { useState, useRef, useEffect } from "react"
import styled from "styled-components"
import {
  AppFunctionComponent,
  ContentfulImage,
  GatsbyImage,
} from "../../../types"
import { Image } from "../../../components/common/image.component"
import { ContentfulVideo } from "../../../../graphql-types"
import { getImage } from "gatsby-plugin-image"
import { useInView } from "react-intersection-observer"

// New styled components based on the design
const UgcItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 300px;
  border-radius: 16px;
  min-height: 532px;
  overflow: hidden;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0px 8px 30px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    max-width: 276px;
    min-height: 494px;
    margin: 0 auto;
  }
`

const MediaContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 532px;
  overflow: hidden;
`

const StyledImage = styled(Image)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`

const FallbackImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`

const VideoContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.color.backgroundSuperLight};

  > div {
    width: 100% !important;
    height: 100% !important;
  }
`

const CustomVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  background-color: ${({ theme }) => theme.color.backgroundSuperLight};

  &::-webkit-media-controls-start-playback-button {
    display: none;
  }
`

const VideoOverlay = styled.div<{ $isHovering: boolean; $hasSound: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ $isHovering, $hasSound }) =>
    $hasSound
      ? "transparent"
      : $isHovering
      ? "rgba(0, 0, 0, 0.42)"
      : "transparent"};
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $isHovering, $hasSound }) =>
    $isHovering || $hasSound ? 1 : 0};
  pointer-events: ${({ $isHovering, $hasSound }) =>
    $isHovering || $hasSound ? "auto" : "none"};
`

const ControlButton = styled.button<{ $center?: boolean }>`
  background-color: transparent;
  color: white;
  border: none;
  border-radius: 50%;
  width: ${({ $center }) => ($center ? "80px" : "60px")};
  height: ${({ $center }) => ($center ? "80px" : "60px")};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.3s ease;
  position: absolute;
  ${({ $center }) =>
    $center
      ? `
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `
      : `
    bottom: 16px;
    right: 16px;
  `}

  &:hover {
  }

  svg {
    width: ${({ $center }) => ($center ? "48px" : "20px")};
    height: ${({ $center }) => ($center ? "48px" : "20px")};
    fill: currentColor;
  }
`

const PauseIcon = () => (
  <svg viewBox="0 0 24 24">
    <rect x="6" y="4" width="4" height="16" fill="currentColor" />
    <rect x="14" y="4" width="4" height="16" fill="currentColor" />
  </svg>
)

const PlayIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z" fill="currentColor" />
  </svg>
)

const UnmuteIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="48"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
  >
    <path
      d="M43.414 40.5859L39.368 36.5399C42.3689 33.0526 44.0126 28.6007 43.998 23.9999C43.998 15.8179 39.054 9.09394 32 5.99994V9.99994C36.774 12.7719 39.998 18.0939 39.998 23.9999C39.9892 27.5531 38.8144 31.0051 36.654 33.8259L34.084 31.2559C35.288 29.0719 36 26.3799 36 23.9999C36 20.4579 34.45 16.1999 32 13.9999V29.1719L28 25.1719V7.99994C27.9998 7.63817 27.9015 7.28323 27.7155 6.97291C27.5296 6.66259 27.2629 6.40853 26.944 6.23777C26.6251 6.06702 26.2658 5.98597 25.9044 6.00326C25.5431 6.02054 25.1932 6.13552 24.892 6.33594L15.454 12.6259L7.414 4.58594L4.586 7.41394L40.586 43.4139L43.414 40.5859ZM24 11.7359V21.1719L18.338 15.5099L24 11.7359ZM8 33.9999H13.394L24.89 41.6639C25.1916 41.8642 25.5417 41.9792 25.9032 41.9968C26.2648 42.0144 26.6244 41.934 26.944 41.7639C27.2632 41.593 27.53 41.3387 27.716 41.028C27.902 40.7173 28.0001 40.362 28 39.9999V36.2419L24 32.2419V36.2639L15.11 30.3359C15.06 30.3019 14.998 30.2959 14.946 30.2699C14.7098 30.1413 14.4498 30.0624 14.182 30.0379C14.118 30.0319 14.064 29.9999 14 29.9999H8V17.9999H9.758L6.204 14.4459C5.54378 14.7764 4.9882 15.2836 4.59909 15.9111C4.20997 16.5385 4.00258 17.2616 4 17.9999V29.9999C4 32.2059 5.794 33.9999 8 33.9999Z"
      fill="white"
    />
  </svg>
)

// Types
type ImageResolverProps = (GatsbyImage | ContentfulImage) & {
  imageWidth?: number
}

type ContentType = ContentfulImage | GatsbyImage | ContentfulVideo

type UgcItemProps = {
  image: ContentType
}

// Helper functions
const isGatsbyImage = (
  image: ContentfulImage | GatsbyImage
): image is GatsbyImage => {
  return Boolean((image as GatsbyImage).gatsbyImageData)
}

const isContentfulVideo = (
  content: ContentType
): content is ContentfulVideo => {
  if (!content || typeof content !== "object") return false
  if (!("file" in content)) return false
  const file = (content as any).file
  return Boolean(
    file &&
      typeof file === "object" &&
      file.url &&
      file.contentType === "video/mp4"
  )
}

// Check if we're on mobile
const isMobile = () => {
  if (typeof window !== "undefined") {
    return window.innerWidth <= 768
  }
  return false
}

// Image resolver component
export const ItemImageResolver: AppFunctionComponent<ImageResolverProps> = (
  itemImage
) => {
  if (isGatsbyImage(itemImage)) {
    const gatsbyImageData = getImage(itemImage.gatsbyImageData)
    if (gatsbyImageData) {
      return (
        <StyledImage
          image={gatsbyImageData}
          alt={itemImage.description || ""}
        />
      )
    }
  }

  // Fallback for ContentfulImage or if Gatsby image processing fails
  return (
    <FallbackImage src={itemImage.file.url} alt={itemImage.description || ""} />
  )
}

// Enhanced Video Component with custom behavior
const EnhancedVideo: AppFunctionComponent<{
  videoUrl: string
}> = ({ videoUrl }) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(true)
  const [hasSound, setHasSound] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isManuallyPaused, setIsManuallyPaused] = useState(false)
  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: false,
  })

  // Initialize video to first frame when loaded
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0
      videoRef.current.preload = "auto"
    }
  }, [])

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0
    }
    setIsLoaded(true)
  }

  const playVideo = async () => {
    if (videoRef.current) {
      try {
        await videoRef.current.play()
        setIsPlaying(true)
        setIsManuallyPaused(false)
      } catch (error) {
        console.error("Play error:", error)
      }
    }
  }

  const pauseVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause()
      setIsPlaying(false)
    }
  }

  // Handle hover/inView state for both desktop and mobile
  useEffect(() => {
    if (!videoRef.current || !isLoaded) return

    const shouldAutoPlay = isMobile() ? inView : isHovering
    const shouldPause = isMobile() ? !inView : !isHovering

    if (shouldAutoPlay && !isPlaying && !hasSound && !isManuallyPaused) {
      videoRef.current.muted = true
      playVideo()
    } else if (shouldPause && !hasSound) {
      pauseVideo()
    }
  }, [isHovering, inView, isLoaded, hasSound, isManuallyPaused])

  // Handle hover state for desktop
  const handleMouseEnter = () => {
    if (!isMobile()) {
      setIsHovering(true)
    }
  }

  const handleMouseLeave = () => {
    if (!isMobile()) {
      setIsHovering(false)
    }
  }

  const handleToggleMute = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (videoRef.current) {
      const newMutedState = !isMuted
      setIsMuted(newMutedState)
      setHasSound(!newMutedState)
      videoRef.current.muted = newMutedState
      setIsManuallyPaused(false)

      // Ensure video is playing when unmuting
      if (!newMutedState && !isPlaying) {
        playVideo()
      }
    }
  }

  const handleTogglePlay = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (videoRef.current) {
      if (isPlaying) {
        pauseVideo()
        setIsManuallyPaused(true)
      } else {
        playVideo()
      }
    }
  }

  return (
    <div
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <CustomVideo
        ref={videoRef}
        src={videoUrl}
        preload="auto"
        muted={isMuted}
        loop
        playsInline
        onClick={(e) => e.stopPropagation()}
        onLoadedMetadata={handleLoadedMetadata}
      />
      <VideoOverlay
        $isHovering={isMobile() ? inView : isHovering}
        $hasSound={hasSound}
      >
        {hasSound && (
          <ControlButton $center onClick={handleTogglePlay}>
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </ControlButton>
        )}
        {!hasSound && (isMobile() ? inView : isHovering) && (
          <ControlButton $center onClick={handleToggleMute}>
            <UnmuteIcon />
          </ControlButton>
        )}
      </VideoOverlay>
    </div>
  )
}

// Content resolver component
const ItemContentResolver: AppFunctionComponent<ContentType> = (content) => {
  if (isContentfulVideo(content)) {
    const videoData = content as unknown as {
      videoTitle?: string
      videoUrl?: string
      file?: {
        url?: string
        contentType?: string
      }
      videoDescription?: {
        videoDescription?: string
      }
      autoplay?: boolean
      loop?: boolean
      controlsPanel?: boolean
    }

    const videoUrl = videoData.file?.url || videoData.videoUrl || ""

    return (
      <VideoContainer>
        <EnhancedVideo videoUrl={videoUrl} />
      </VideoContainer>
    )
  } else {
    return <ItemImageResolver {...(content as GatsbyImage | ContentfulImage)} />
  }
}

// Main component
const UgcItem: AppFunctionComponent<UgcItemProps> = ({ image }) => {
  return (
    <UgcItemContainer>
      <MediaContainer>
        {image && <ItemContentResolver {...(image as any)} />}
      </MediaContainer>
    </UgcItemContainer>
  )
}

export default UgcItem
